import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import { FiEdit2, FiSend, FiRefreshCw, FiCheckCircle, FiAlertTriangle } from 'react-icons/fi';
import { BackButton, CustomPagination } from '../../components';
import { useStateContext } from '../../contexts/ContextProvider';
import useKYCStatusRedirect from '../../hooks/useKYCStatusRedirect';
import { userRequest } from '../../utils/requestMethods';
import { formatCurrency } from '../../utils/currencyUtils';
import { getEducationLevelLabel, getFundEducationLevels } from '../../utils/enrollmentUtils';
import SkeletonTable from '../../components/common/SkeletonTable';
import StatCard from '../../components/common/StatCard';
import EditFundModal from '../../components/Finance/EditFundModal';
import FundWithdrawModal from '../../components/Finance/FundWithdrawModal';
import SyncBalanceConfirmModal from '../../components/Finance/SyncBalanceConfirmModal';

const customStyles = {
  headRow: {
    style: { backgroundColor: 'rgb(248, 250, 252)', fontWeight: 'bold' },
  },
};

const TX_TYPE_LABELS = {
  payment_in: 'Payment In',
  withdrawal: 'Withdrawal',
  transfer: 'Transfer',
  fee: 'Fee',
};

// Direction comes from the sign of `amount`, not the type: a transfer is a
// credit on the receiving fund and a debit on the sending one, so the same
// type appears in both directions.
const isCredit = (row) => (Number(row?.amount) || 0) >= 0;

const FundDetail = ({ history }) => {
  useKYCStatusRedirect('/finance/funds');
  const { fundId } = useParams();
  const { user, school } = useStateContext();
  const schoolId = school?._id;
  const isOwner = user?.role === 0;

  const [fund, setFund] = useState(null);
  const [balance, setBalance] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [volume, setVolume] = useState(null);
  const [reconciliation, setReconciliation] = useState(null);
  const [typeFilter, setTypeFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isWithdrawOpen, setIsWithdrawOpen] = useState(false);
  const [isSyncOpen, setIsSyncOpen] = useState(false);

  const fetchFund = async () => {
    if (!schoolId || !fundId) return;
    try {
      const { data } = await userRequest.get(`/schools/${schoolId}/funds/${fundId}`);
      setFund(data.fund);
      setBalance(data.balance);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchTransactions = async () => {
    if (!schoolId || !fundId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '100', page: '1' });
      if (typeFilter) params.set('type', typeFilter);
      const { data } = await userRequest.get(`/schools/${schoolId}/funds/${fundId}/transactions?${params.toString()}`);
      setTransactions(data.transactions || []);
      setVolume(data.volume || null);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReconciliation = async () => {
    if (!schoolId || !fundId) return;
    try {
      const { data } = await userRequest.get(`/schools/${schoolId}/funds/${fundId}/reconciliation`);
      setReconciliation(data);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchFund();
    fetchReconciliation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId, fundId]);

  useEffect(() => {
    fetchTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId, fundId, typeFilter]);

  const columns = [
    {
      name: 'Type',
      cell: row => (
        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${
          isCredit(row) ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
        }`}>
          {TX_TYPE_LABELS[row.type] || row.type?.replace(/_/g, ' ') || '—'}
        </span>
      ),
    },
    {
      name: 'Amount',
      cell: row => {
        const credit = isCredit(row);
        return (
          <span className={`font-semibold whitespace-nowrap ${credit ? 'text-green-700' : 'text-red-600'}`}>
            {credit ? '+' : '−'}{formatCurrency(Math.abs(Number(row.amount) || 0))}
          </span>
        );
      },
    },
    {
      name: 'Description',
      cell: row => <span className="text-sm text-gray-600">{row.description || '—'}</span>,
    },
    {
      name: 'Balance After',
      cell: row => <span className="text-sm text-gray-500">{formatCurrency(row.balanceAfter)}</span>,
    },
    {
      name: 'Date',
      cell: row => new Date(row.transactionDate).toLocaleDateString('en-NG', { dateStyle: 'medium' }),
      sortable: true,
    },
  ];

  return (
    <div className="m-2 md:m-10 mt-24 p-4 md:p-10 dark:bg-secondary-dark-bg bg-white rounded-3xl min-h-screen">
      <BackButton history={history} />

      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-bold text-gray-900">{fund?.name || 'Fund'}</h2>
            {fund?.is_default && (
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">Default</span>
            )}
          </div>
          {getFundEducationLevels(fund).length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {getFundEducationLevels(fund).map(level => (
                <span key={level} className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">
                  {getEducationLevelLabel(level)}
                </span>
              ))}
            </div>
          )}
          {fund?.description && <p className="text-sm text-gray-500 mt-1">{fund.description}</p>}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setIsEditOpen(true)}
            className="inline-flex items-center gap-2 py-2 px-4 text-sm font-medium rounded-full text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200 transition-colors"
          >
            <FiEdit2 className="w-4 h-4" />
            Edit
          </button>
          <button
            type="button"
            disabled={!isOwner}
            title={!isOwner ? 'Only the school owner can withdraw' : ''}
            onClick={() => setIsWithdrawOpen(true)}
            className="inline-flex items-center gap-2 py-2 px-4 text-sm font-medium rounded-full text-white bg-indigo-600 hover:bg-indigo-700 shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiSend className="w-4 h-4" />
            Withdraw
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Available Balance" value={formatCurrency(balance?.availableBalance)} color="blue" />
        <StatCard label="Ledger Balance" value={formatCurrency(balance?.ledgerBalance)} color="indigo" />
        <StatCard label="Total Credited" value={formatCurrency(balance?.totalCredited)} color="green" />
        <StatCard label="Total Debited" value={formatCurrency(balance?.totalDebited)} color="red" />
      </div>

      {reconciliation && (
        <div className={`flex items-center justify-between gap-3 rounded-2xl border px-5 py-4 mb-6 ${
          reconciliation.inSync ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'
        }`}>
          <div className="flex items-center gap-3">
            {reconciliation.inSync ? (
              <FiCheckCircle className="text-green-600 text-lg" />
            ) : (
              <FiAlertTriangle className="text-yellow-600 text-lg" />
            )}
            <div>
              <p className={`text-sm font-semibold ${reconciliation.inSync ? 'text-green-700' : 'text-yellow-700'}`}>
                {reconciliation.inSync ? 'Balance in sync' : `Drift detected: ${formatCurrency(reconciliation.drift)}`}
              </p>
              <p className="text-xs text-gray-500">Cached {formatCurrency(reconciliation.cachedBalance)} · Ledger {formatCurrency(reconciliation.ledgerBalance)}</p>
            </div>
          </div>
          {!reconciliation.inSync && (
            <button
              type="button"
              disabled={!isOwner}
              title={!isOwner ? 'Only the school owner can sync balance' : ''}
              onClick={() => setIsSyncOpen(true)}
              className="inline-flex items-center gap-2 py-2 px-4 text-sm font-medium rounded-full text-white bg-yellow-600 hover:bg-yellow-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FiRefreshCw className="w-4 h-4" />
              Fix Balance
            </button>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="text-sm font-semibold text-gray-700">Transactions</p>
        <div className="flex items-center gap-3">
          {volume && (
            <p className="text-xs text-gray-500">
              In {formatCurrency(volume.in)} · Out {formatCurrency(volume.out)}
            </p>
          )}
          <select
            value={typeFilter}
            onChange={e => setTypeFilter(e.target.value)}
            className="rounded-xl bg-gray-50 border border-gray-200 text-sm px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="">All Types</option>
            <option value="credit">Credit</option>
            <option value="debit">Debit</option>
          </select>
        </div>
      </div>

      <div className="border border-gray-200 rounded-2xl overflow-hidden">
        <DataTable
          data={transactions}
          columns={columns}
          getRowId={row => row.id}
          pagination
          paginationComponent={CustomPagination}
          paginationRowsPerPageOptions={[10, 20, 30, 50]}
          customStyles={customStyles}
          progressPending={loading}
          progressComponent={<SkeletonTable rows={5} columns={5} />}
          highlightOnHover
          persistTableHead
          noDataComponent={
            <div className="py-14 text-center text-sm text-gray-400">No transactions found.</div>
          }
        />
      </div>

      {isEditOpen && fund && (
        <EditFundModal
          isOpen={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          schoolId={schoolId}
          fund={fund}
          onUpdated={fetchFund}
        />
      )}

      {isWithdrawOpen && (
        <FundWithdrawModal
          isOpen={isWithdrawOpen}
          onClose={() => setIsWithdrawOpen(false)}
          school={school}
          schoolId={schoolId}
          fundId={fundId}
          availableBalance={balance?.availableBalance}
          onSuccess={() => { fetchFund(); fetchTransactions(); }}
        />
      )}

      {isSyncOpen && (
        <SyncBalanceConfirmModal
          isOpen={isSyncOpen}
          onClose={() => setIsSyncOpen(false)}
          schoolId={schoolId}
          fundId={fundId}
          onSynced={() => { fetchReconciliation(); fetchFund(); }}
        />
      )}
    </div>
  );
};

export default FundDetail;
